import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MdAdd, MdDelete, MdEditNote, MdClose, MdRefresh } from "react-icons/md";
import { BsFlower3 } from "react-icons/bs";
import { GiGraveFlowers } from "react-icons/gi";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { productImageStyle } from "../productImage";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/permissions";
import "../products.css";

/* ------------------------------------------------------------------ *
 * Products (redesign) — /products
 *
 * Replaces src/pages/productListPage.js (kept at /products-v1):
 *   - bootstrap rows of .selectable-card  -> an image-led catalogue grid
 *   - a hard-coded list of nine product
 *     types behind a "Filter by Type"
 *     popover                             -> a type rail built from
 *                                            settings.product_types
 *   - ProductDetailSide (a slide-over of
 *     SummaryTables)                      -> ProductDetailDrawer, with
 *                                            per-size prices and the
 *                                            flower content for each
 *   - edit opened a second browser tab    -> in-app navigation
 *
 * Same endpoints as the legacy page: POST /products/getAllBy
 * ({ finder, page, limit }, where finder takes product_type and a
 * $text search), GET /products/delete/:id, GET /settings/getAll.
 * ------------------------------------------------------------------ */

const PAGE_LIMIT = 18;

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const money = (v) => {
  const n = Number(v);
  return isFinite(n) ? `$${n % 1 === 0 ? n : n.toFixed(2)}` : `$${v}`;
};

/* Sizes are stored as { size: price }. The card shows the span rather
   than one number, because most products are priced per size. */
export function priceRange(sizesRaw) {
  const sizes = safeParse(sizesRaw, {}) || {};
  const values = Object.values(sizes)
    .map((v) => Number(v))
    .filter((n) => isFinite(n));
  if (!values.length) return { label: "", count: Object.keys(sizes).length };
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return {
    label: lo === hi ? money(lo) : `${money(lo)} – ${money(hi)}`,
    count: Object.keys(sizes).length,
  };
};

export function ProductIcon({ type }) {
  return String(type || "").toLowerCase() === "casket cover" ? (
    <GiGraveFlowers />
  ) : (
    <BsFlower3 />
  );
}

export function ProductsRedesign() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const canWrite = can(user, "products:write");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);

  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(null);

  /* Type-ahead, debounced onto the endpoint's $text search. */
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status !== "success") return;
        setTypes(safeParse((doc.data.response[0] || {}).product_types, []) || []);
      })
      .catch(() => {});
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .post(BASE_SERVER_URL + "/products/getAllBy", {
        finder: {
          ...(type ? { product_type: type } : {}),
          ...(query ? { $text: { $search: `"${query}"` } } : {}),
        },
        page,
        limit: PAGE_LIMIT,
      })
      .then((res) => {
        setProducts(res.data.response || []);
        setTotal(res.data.responseLength || 0);
        setPages(Math.ceil((res.data.responseLength || 0) / PAGE_LIMIT));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [type, query, page]);

  useEffect(refresh, [refresh]);

  const pageList = useMemo(() => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out = [1];
    const from = Math.max(2, page - 1);
    const to = Math.min(pages - 1, page + 1);
    if (from > 2) out.push("…");
    for (let i = from; i <= to; i += 1) out.push(i);
    if (to < pages - 1) out.push("…");
    out.push(pages);
    return out;
  }, [pages, page]);

  function pickType(next) {
    setType(next);
    setPage(1);
  }

  return (
    <div className="pr-page">
      {/* ---------------- toolbar ---------------- */}
      <div className="pr-bar">
        <label className="pr-search">
          <span className="pr-search-icon" />
          <input
            type="search"
            value={search}
            placeholder="Search products by name, code or description"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <span className="pr-count rf-num">
          {loading ? "…" : `${total} product${total === 1 ? "" : "s"}`}
        </span>

        <span className="rf-spacer" />

        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={refresh}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
        {canWrite && (
          <button
            type="button"
            className="rf-btn rf-btn-primary"
            onClick={() => navigate("/products/new")}
          >
            <MdAdd style={{ verticalAlign: "-2px" }} /> New product
          </button>
        )}
      </div>

      {/* ---------------- type rail ---------------- */}
      <div className="pr-types">
        <button
          type="button"
          className={`pr-type${type === "" ? " is-active" : ""}`}
          onClick={() => pickType("")}
        >
          All types
        </button>
        {types.map((t) => (
          <button
            key={t}
            type="button"
            className={`pr-type${type === t ? " is-active" : ""}`}
            onClick={() => pickType(type === t ? "" : t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ---------------- grid ---------------- */}
      <div className="pr-body">
        {loading && <div className="rf-empty">Loading products…</div>}

        {!loading && loadError && (
          <div className="rf-empty">
            Couldn&apos;t load products.{" "}
            <button type="button" className="rf-btn rf-btn-ghost" onClick={refresh}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && products.length === 0 && (
          <div className="rf-empty">
            No products found
            {type ? ` in ${type}` : ""}
            {query ? ` matching “${query}”` : ""}.
          </div>
        )}

        {!loading && !loadError && products.length > 0 && (
          <div className="pr-grid">
            {products.map((p) => {
              const range = priceRange(p.sizes);
              return (
                <button
                  type="button"
                  key={p._id}
                  className={`pr-card${open && open._id === p._id ? " is-active" : ""}`}
                  onClick={() => setOpen(p)}
                >
                  <span className="pr-thumb" style={productImageStyle(p, "thumb")}>
                    {!p.image && <ProductIcon type={p.product_type} />}
                    {p.code && <span className="pr-code">{p.code}</span>}
                  </span>
                  <span className="pr-card-body">
                    <span className="pr-name">{p.product_name || "Untitled"}</span>
                    <span className="pr-sizes rf-num">
                      {range.count
                        ? `${range.count} size${range.count === 1 ? "" : "s"}`
                        : "No sizes set"}
                    </span>
                    <span className="pr-meta">
                      <span className="pr-tag">{p.product_type || "No type"}</span>
                      <span className={`pr-price${range.label ? "" : " is-empty"}`}>
                        {range.label || "—"}
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {!loading && !loadError && pages > 1 && (
          <div className="pr-pager">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>
            {pageList.map((p, i) =>
              p === "…" ? (
                <button type="button" key={`gap-${i}`} disabled>
                  …
                </button>
              ) : (
                <button
                  type="button"
                  key={p}
                  className={p === page ? "is-active" : ""}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              disabled={page === pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              ›
            </button>
          </div>
        )}
      </div>

      {open && (
        <ProductDetailDrawer
          product={open}
          canWrite={canWrite}
          onClose={() => setOpen(null)}
          onEdit={() => navigate("/products/" + open._id + "/edit")}
          onDeleted={() => {
            toast.success("Product deleted.");
            setOpen(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * ProductDetailDrawer — replaces ProductDetailSide in components.js.
 * The legacy panel showed description/code/type in a SummaryTable and
 * the sizes in a tab strip; this keeps all of it and adds the flower
 * content per size, which is the thing the shop actually looks up.
 * ------------------------------------------------------------------ */
export function ProductDetailDrawer({ product, canWrite, onClose, onEdit, onDeleted }) {
  const sizes = useMemo(() => safeParse(product.sizes, {}) || {}, [product]);
  /* `flower_content` is the per-size array of {flower_id, flower_name,
     amount}; older products only have the `content` map of name->amount,
     so fall back to that rather than showing nothing. */
  const content = useMemo(() => {
    const arrayed = product.flower_content || {};
    if (Object.keys(arrayed).length) return arrayed;
    const legacy = safeParse(product.content, {}) || {};
    const out = {};
    Object.keys(legacy).forEach((size) => {
      if (size === "arrayed" || typeof legacy[size] !== "object") return;
      out[size] = Object.keys(legacy[size]).map((name) => ({
        flower_name: name,
        amount: legacy[size][name],
      }));
    });
    return out;
  }, [product]);

  const sizeKeys = Object.keys(sizes);
  const [active, setActive] = useState(sizeKeys[0] || "");
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function remove() {
    setDeleting(true);
    setError("");
    axios
      .get(BASE_SERVER_URL + "/products/delete/" + product._id)
      .then((res) => {
        setDeleting(false);
        if (res.data.status === "success") onDeleted();
        else setError("The server refused the delete. Please try again.");
      })
      .catch(() => {
        setDeleting(false);
        setError("Couldn't reach the server.");
      });
  }

  const flowers = content[active] || [];

  return (
    <div className="pd-wrap" role="dialog" aria-modal="true">
      <div className="pd-scrim" onClick={onClose} />
      <div className="pd-panel">
        <header className="pd-head">
          <div className="pd-head-main">
            <div className="pd-eyebrow">{product.product_type || "Product"}</div>
            <h3 className="pd-title">{product.product_name || "Untitled"}</h3>
          </div>
          <div className="pd-actions">
            {canWrite && (
              <button
                type="button"
                className="pd-icon-btn"
                title="Edit this product"
                onClick={onEdit}
              >
                <MdEditNote />
              </button>
            )}
            {canWrite && (
              <button
                type="button"
                className="pd-icon-btn is-danger"
                title="Delete this product"
                onClick={() => setConfirming(true)}
              >
                <MdDelete />
              </button>
            )}
            <button type="button" className="pd-icon-btn" title="Close" onClick={onClose}>
              <MdClose />
            </button>
          </div>
        </header>

        {confirming && (
          <div className="pd-confirm">
            <span>
              {error || (
                <>
                  Delete <b>{product.product_name}</b>? Orders already placed keep
                  their copy.
                </>
              )}
            </span>
            <span className="rf-spacer" />
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={() => {
                setConfirming(false);
                setError("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn pd-btn-danger"
              onClick={remove}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        )}

        <div className="pd-body">
          <div className="pd-hero" style={productImageStyle(product, "medium")}>
            {!product.image && <ProductIcon type={product.product_type} />}
          </div>

          <div className="pd-sec">Details</div>
          <dl className="pd-facts">
            <dt>Code</dt>
            <dd className={product.code ? "" : "is-empty"}>{product.code || "—"}</dd>
            <dt>Type</dt>
            <dd className={product.product_type ? "" : "is-empty"}>
              {product.product_type || "—"}
            </dd>
            <dt>Description</dt>
            <dd className={product.description ? "" : "is-empty"}>
              {product.description || "No description."}
            </dd>
          </dl>

          <div className="pd-sec">Sizes &amp; prices</div>
          {sizeKeys.length === 0 ? (
            <div className="rf-empty">No sizes priced for this product.</div>
          ) : (
            <>
              <div className="pd-tabs">
                {sizeKeys.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`pd-tab${s === active ? " is-active" : ""}`}
                    onClick={() => setActive(s)}
                  >
                    <span className="pd-tab-k">{s}</span>
                    <span className="pd-tab-v">{money(sizes[s])}</span>
                  </button>
                ))}
              </div>

              <div className="pd-sec">Flower content — {active || "—"}</div>
              {flowers.length === 0 ? (
                <div className="rf-empty">No flower content recorded for this size.</div>
              ) : (
                <div className="pd-flowers">
                  {flowers.map((f, i) => (
                    <div className="pd-flower" key={f.flower_id || i}>
                      <span className="pd-flower-n rf-num">{i + 1}</span>
                      <span className="pd-flower-name">
                        {f.flower_name || "Unnamed flower"}
                      </span>
                      <span className="pd-flower-amt rf-num">×{f.amount || 0}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductsRedesign;
